// Acting roles, the role home and its "prepared for you" queue (docs/pipeline/V1-CONTRACT.md,
// "Roles and acting roles"). The server holds the acting state and decides access; these helpers only
// drive what the UI shows.
import { useQuery } from "@tanstack/react-query";
import { api } from "./api";
import type { ActingSeat, Me, Role } from "./types";

export type SeatStatus = "available" | "later";

export interface Seat {
  key: string;
  short: string;
  label: string;
  status: SeatStatus;
}

export interface SeatChoice {
  role: string;
  short: string;
  label: string;
  status: SeatStatus;
  /** The caller holds an active grant for this seat. */
  granted: boolean;
}

export interface MyActing {
  real_role: Role;
  acting: ActingSeat | null;
  seats: SeatChoice[];
}

export type SourceStatus = "connected" | "waiting_for_source" | "not_in_pass";

export interface RoleSource {
  key: string;
  label: string;
  status: SourceStatus;
  count: number | null;
  link: string | null;
  explanation: string;
}

export interface QueueItem {
  id: string;
  seat_role: string;
  loan_id: string | null;
  source: string;
  requested_action: "approve" | "send" | "delegate";
  title: string;
  summary: string | null;
  prepared_by: string;
  proposer_user_id: string | null;
  status: "open" | "approved" | "declined" | "withdrawn";
  decided_by: string | null;
  decided_as_role: string | null;
  decided_at: string | null;
  lending_decision: "not_made";
  created_at: string;
}

export interface WalkthroughProgress {
  role: string;
  step: number;
  completed_at: string | null;
  dismissed_at: string | null;
}

export interface RoleHome {
  role: Role;
  label: string;
  short: string | null;
  real_role: Role;
  acting: ActingSeat | null;
  lending_decision: "not_made";
  system_of_record: string;
  sources: RoleSource[];
  items: QueueItem[];
  walkthrough: WalkthroughProgress | null;
}

export interface ActingGrant {
  id: string;
  user_id: string;
  display_name: string;
  real_role: Role;
  acting_role: Role;
  granted_by: string;
  granted_at: string;
}

export interface ActingAdmin {
  seats: Seat[];
  grants: ActingGrant[];
  members: { id: string; display_name: string; role: Role }[];
}

export const actingKeys = {
  mine: ["acting", "mine"] as const,
  admin: ["acting", "admin"] as const,
  home: (role: string) => ["role-home", role] as const,
};

export function useMyActing(enabled = true) {
  return useQuery({ queryKey: actingKeys.mine, queryFn: () => api<MyActing>("/api/me/acting-role"), enabled, staleTime: 30_000 });
}

export function useRoleHome(role: string) {
  return useQuery({ queryKey: actingKeys.home(role), queryFn: () => api<RoleHome>("/api/role-home"), staleTime: 15_000 });
}

export function useActingAdmin(enabled: boolean) {
  return useQuery({ queryKey: actingKeys.admin, queryFn: () => api<ActingAdmin>("/api/admin/acting-roles"), enabled });
}

/**
 * Starts (or switches) acting, then reloads the app: every cached query, the live feed and the
 * navigation belong to the previous seat, so a clean start is the honest way to show the new one.
 */
export async function startActing(role: string, reload: (to: string) => void = (to) => window.location.assign(to)) {
  await api<MyActing>("/api/me/acting-role", { method: "POST", body: { role } });
  reload("/role");
}

export async function stopActing(reload: (to: string) => void = (to) => window.location.assign(to)) {
  await api<MyActing>("/api/me/acting-role", { method: "DELETE" });
  reload("/role");
}

export function saveWalkthrough(role: string, body: { step: number; completed?: boolean; dismissed?: boolean }) {
  return api<WalkthroughProgress>(`/api/me/walkthroughs/${encodeURIComponent(role)}`, { method: "PUT", body });
}

export function decideItem(id: string, decision: "approve" | "decline") {
  return api<QueueItem>(`/api/role-queue/${id}/decision`, { method: "POST", body: { decision } });
}

/** Seats the person may switch into now (granted, available, not the one they are in). */
export function switchableSeats(mine: MyActing | undefined): SeatChoice[] {
  if (!mine) return [];
  return mine.seats.filter((s) => s.granted && s.status === "available" && s.role !== mine.acting?.role);
}

/** The header chip: always visible while acting. */
export function actingChipText(acting: ActingSeat | null | undefined): string | null {
  return acting ? `Acting as ${acting.short}` : null;
}

/** The banner's sentence, naming the real person and the seat. */
export function actingBannerText(me: Me): string | null {
  const a = me.user.acting;
  if (!a) return null;
  return `You are acting as ${a.label.toLowerCase()}. LoanShip records you, ${me.user.display_name}, plus this seat on everything you do.`;
}

/** First visit opens the walkthrough; finishing or dismissing it keeps it closed until reopened. */
export function shouldAutoOpenWalkthrough(w: WalkthroughProgress | null | undefined): boolean {
  return !w || (w.completed_at === null && w.dismissed_at === null);
}

export const SOURCE_STATUS_LABEL: Record<SourceStatus, string> = {
  connected: "In LoanShip",
  waiting_for_source: "Waiting for a source",
  not_in_pass: "Outside LoanShip during the pilot",
};

export const ACTION_LABEL: Record<QueueItem["requested_action"], string> = {
  approve: "Approve",
  send: "Approve and send",
  delegate: "Approve the delegation",
};

/** The superadmin's grant table: one row per pilot member, one cell per available seat. */
export function grantMatrix(data: ActingAdmin) {
  const seats = data.seats.filter((s) => s.status === "available");
  const granted = new Set(data.grants.map((g) => `${g.user_id}:${g.acting_role}`));
  const rows = data.members.map((m) => ({
    ...m,
    cells: seats.map((seat) => ({
      seat,
      // Nobody acts in their own role's seat.
      allowed: m.role !== seat.key,
      granted: granted.has(`${m.id}:${seat.key}`),
    })),
  }));
  return { seats, rows };
}

/** People a manager can assign to a loan: in their own assignable role, or in an assignable acting seat. */
export function seatCandidates(
  users: { id: string; display_name: string; role: string; status: string }[],
  roles: { role: string; assignable: boolean }[],
  actingSeats: { user_id: string; role: string }[],
  assigned: { user_id: string }[],
) {
  const assignable = new Set(roles.filter((r) => r.assignable).map((r) => r.role));
  const taken = new Set(assigned.map((a) => a.user_id));
  const out: { user_id: string; display_name: string; role: string; acting: boolean }[] = [];
  for (const u of users) {
    if (u.status !== "active" || taken.has(u.id)) continue;
    if (assignable.has(u.role)) out.push({ user_id: u.id, display_name: u.display_name, role: u.role, acting: false });
    for (const s of actingSeats) {
      if (s.user_id === u.id && assignable.has(s.role) && s.role !== u.role) out.push({ user_id: u.id, display_name: u.display_name, role: s.role, acting: true });
    }
  }
  return out.sort((a, b) => a.display_name.localeCompare(b.display_name) || a.role.localeCompare(b.role));
}

export interface AgentWorkLaneSpec {
  id: string;
  mode: "AGENT:AUTO" | "AGENT:DRAFT";
  title: string;
  description: string;
  feedsRoles: Role[];
}

export interface RoleEmailWorkSpec {
  role: Role;
  shortLabel: string;
  seatLabel: string;
  requestedAction: QueueItem["requested_action"];
  agentPrepares: string[];
  humanDecision: string;
}

export interface CounterpartyEmailWorkSpec {
  counterparty: "loan_officer" | "broker";
  label: string;
  stage: "Pre-Deal Scenario" | "Active Deal";
  route: string;
  actionSummary: string;
  gatedBySeat: Role;
}

export const LENDY_EMAIL_AGENT_WORK_LANES: AgentWorkLaneSpec[] = [
  {
    id: "attachment_hygiene_pdf_slice",
    mode: "AGENT:AUTO",
    title: "Attachment Hygiene & PDF Page Slicer",
    description:
      "Drops inline email signature noise (e.g. image001.png), deduplicates by SHA-256, extracts cited PDF page ranges ('page 3'), and files substantive docs into Smart Drop categories.",
    feedsRoles: ["credit_analyst", "account_manager", "underwriter"],
  },
  {
    id: "dual_track_pricing_exception_eval",
    mode: "AGENT:AUTO",
    title: "Dual-Track Pricing & Exception Tier Evaluator",
    description:
      "Evaluates Track 1 (Ellington/Spark) + Track 2 (Loannex) against cited snapshots, classifies Exception Tier (In-Box vs. Senior UW vs. Senior UW + Management), and attaches rate-fluctuation disclosures.",
    feedsRoles: ["ae", "underwriter", "operations_manager", "division_manager", "executive"],
  },
  {
    id: "ca_bank_statement_credit_prep",
    mode: "AGENT:DRAFT",
    title: "CA Bank-Statement & 4-Legs Credit Pre-Analysis",
    description:
      "Extracts 12m/24m bank statement deposit streams, NSF flags, and expense-ratio worksheet for the Credit Analyst (CA) before or alongside Underwriting.",
    feedsRoles: ["credit_analyst"],
  },
  {
    id: "am_condition_precheck",
    mode: "AGENT:DRAFT",
    title: "AM Condition Pre-Check & Packaging",
    description:
      "Matches uploaded PDFs and page slices against open loan conditions (/conditions) so the Account Manager (AM) verifies completeness before Underwriting is notified.",
    feedsRoles: ["account_manager"],
  },
  {
    id: "consolidated_reply_and_voice_draft",
    mode: "AGENT:DRAFT",
    title: "Consolidated Reply Draft & AE Assistant Call Script",
    description:
      "Stages a single consolidated counterparty email reply (with the Portal File Submission Link) and optional AE Assistant follow-up call script waiting on the human AE Send Gate.",
    feedsRoles: ["ae"],
  },
];

export const LENDY_EMAIL_ROLE_WORK_SPECS: RoleEmailWorkSpec[] = [
  {
    role: "ae",
    shortLabel: "AE",
    seatLabel: "Account Executive",
    requestedAction: "send",
    agentPrepares: [
      "Consolidated broker/LO reply draft with cited rate-sheet idea & fluctuating-rates disclaimer",
      "Pre-filled Portal File Submission Link to convert scenario into a portal submission",
      "Compensating-factors exception narrative for Senior UW / Management",
    ],
    humanDecision: "Review staged quote/reply and click Approve and send (or trigger AE Assistant call).",
  },
  {
    role: "credit_analyst",
    shortLabel: "CA",
    seatLabel: "Credit Analyst",
    requestedAction: "approve",
    agentPrepares: [
      "Pre-extracted 12m/24m bank statement monthly deposit table & expense-ratio worksheet",
      "4-Legs credit memo (FICO, LTV, reserves, income/DSCR) pre-linked to email attachments",
    ],
    humanDecision: "Verify CA income/credit worksheet and click Approve to attach to the deal for UW.",
  },
  {
    role: "account_manager",
    shortLabel: "AM",
    seatLabel: "Account Manager",
    requestedAction: "approve",
    agentPrepares: [
      "Pre-checked condition evidence (/conditions) with extracted PDF page ranges ('page 3')",
      "Staged missing-stip chaser with capability-scoped Smart Drop link (/smart-drops)",
    ],
    humanDecision: "Approve complete condition evidence to package cleanly for Underwriting.",
  },
  {
    role: "underwriter",
    shortLabel: "UW",
    seatLabel: "Underwriter / Senior UW",
    requestedAction: "approve",
    agentPrepares: [
      "AM-verified PTD condition packet (no raw unverified PDFs)",
      "Standard Exception review packet (SeniorUnderwritingReview) with CA worksheet",
    ],
    humanDecision: "Review AM-verified conditions and Standard Exception package alongside Encompass.",
  },
  {
    role: "operations_manager",
    shortLabel: "OPS / MGMT",
    seatLabel: "Management & Operations",
    requestedAction: "approve",
    agentPrepares: [
      "Bigger Exception escalation packet (SeniorUnderwritingAndManagementApproval)",
      "Cross-seat SLA bottleneck delegation proposals across CA / AM / UW",
    ],
    humanDecision: "Co-approve Bigger Exception packages alongside Senior Underwriting or delegate queue items.",
  },
  {
    role: "doc_funding",
    shortLabel: "DOC",
    seatLabel: "Doc & Funding / Lock Desk",
    requestedAction: "approve",
    agentPrepares: [
      "Rate-lock extension / relock request extracted from [LOCK-DESK] email clauses",
      "Closing disclosure (CD), title CPL, hazard insurance, and funding wire packet",
    ],
    humanDecision: "Approve lock extension or closing/funding checklist item.",
  },
];

export const LENDY_COUNTERPARTY_EMAIL_WORK_SPECS: CounterpartyEmailWorkSpec[] = [
  {
    counterparty: "broker",
    label: "Broker Partner",
    stage: "Pre-Deal Scenario",
    route: "/prospecting",
    actionSummary:
      "Click the Portal File Submission Link from the AE/Lendy reply to upload the 1003 (MISMO 3.4) and submit the file for underwriting/exception review.",
    gatedBySeat: "ae",
  },
  {
    counterparty: "loan_officer",
    label: "Loan Officer (LO)",
    stage: "Active Deal",
    route: "/smart-drops",
    actionSummary:
      "Upload missing PTD/PTF condition documents into pre-labeled Smart Drop slots (/smart-drops) so the AM and Lendy can clear stips without email ping-pong.",
    gatedBySeat: "account_manager",
  },
  {
    counterparty: "loan_officer",
    label: "Loan Officer (LO)",
    stage: "Active Deal",
    route: "/signatures",
    actionSummary:
      "Review the Draft LE explainer and 1003 (/signatures) and complete the LO Electronic Review Attestation to unlock the view-only borrower Draft LE preview.",
    gatedBySeat: "ae",
  },
];

export function emailWorkForRole(role: Role): RoleEmailWorkSpec {
  return (
    LENDY_EMAIL_ROLE_WORK_SPECS.find((s) => s.role === role) ??
    LENDY_EMAIL_ROLE_WORK_SPECS.find((s) => s.role === "operations_manager")!
  );
}
